
import { Peer, DataConnection } from 'peerjs';
import { CHUNK_SIZE } from '../constants';
import { FileTransferMeta, ChatMessage, MessageType } from '../types';

type EventCallback = (data: any) => void;

export class P2PService {
  private peer: Peer | null = null;
  private conn: DataConnection | null = null;
  private callbacks: Map<string, EventCallback[]> = new Map();
  
  // File Transfer State
  private incomingFileMeta: FileTransferMeta | null = null;
  private incomingFileBuffer: (ArrayBuffer | Uint8Array | Blob)[] = [];
  private receivedBytes = 0;

  constructor() {}

  public on(event: string, callback: EventCallback) {
    if (!this.callbacks.has(event)) {
      this.callbacks.set(event, []);
    }
    this.callbacks.get(event)?.push(callback);
  }

  public off(event: string, callback: EventCallback) {
    const listeners = this.callbacks.get(event);
    if (listeners) {
      this.callbacks.set(event, listeners.filter(cb => cb !== callback));
    }
  }

  private emit(event: string, data: any) {
    this.callbacks.get(event)?.forEach(cb => cb(data));
  }

  // Auto-Discovery Session Initialization
  public initSession(roomId: string, myName: string) {
    // Only disconnect if the roomId is different or we are in a broken state
    if (this.peer && !this.peer.destroyed) {
       this.disconnect();
    }

    console.log(`Attempting to claim Room ID: ${roomId}`);

    // 1. Try to initialize as HOST (claim the ID)
    const tempPeer = new Peer(roomId, {
      debug: 1,
      config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:global.stun.twilio.com:3478' }
        ]
      }
    });

    tempPeer.on('open', (id) => {
      console.log('Successfully claimed Room ID. I am HOST.');
      this.peer = tempPeer;
      this.bindHostEvents(myName);
      this.emit('status', 'waiting');
      this.emit('info', `Waiting for partner to join...`);
    });

    tempPeer.on('error', (err: any) => {
      // If ID is taken, it means the Partner is already Hosting.
      // We should switch to CLIENT mode and connect to them.
      if (err.type === 'unavailable-id') {
        console.log('Room ID taken. Switching to CLIENT mode...');
        // Do not destroy tempPeer immediately here as it might be async, 
        // but for 'unavailable-id' it's usually safe to just abandon it and start client.
        this.initAsClient(roomId, myName);
      } else {
        console.error('PeerJS Error:', err);
        this.emit('error', `Connection error: ${err.type}`);
        this.emit('status', 'error');
      }
    });
  }

  private bindHostEvents(myName: string) {
    if (!this.peer) return;

    this.peer.on('connection', (conn) => {
      console.log('Incoming connection from partner...');
      if (this.conn && this.conn.open) {
        conn.close(); // Busy
        return;
      }
      this.setupConnection(conn, myName);
    });

    this.peer.on('disconnected', () => {
       // Only emit if we are truly disconnected and not just temporarily
       if (!this.peer || this.peer.destroyed) {
         this.emit('status', 'disconnected');
       }
    });
  }

  private initAsClient(targetRoomId: string, myName: string) {
    // Create a new Peer with a random ID
    this.peer = new Peer(undefined, {
      debug: 1,
       config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:global.stun.twilio.com:3478' }
        ]
      }
    });

    this.peer.on('open', (id) => {
      console.log('Client Peer ready. Connecting to:', targetRoomId);
      this.emit('status', 'connecting');
      this.connectToPeer(targetRoomId, myName);
    });

    this.peer.on('error', (err) => {
      console.error('Client Peer Error:', err);
      this.emit('error', 'Failed to connect to partner.');
    });
  }

  private connectToPeer(targetId: string, myName: string) {
    if (!this.peer) return;
    
    // reliably: true ensures TCP-like delivery
    const conn = this.peer.connect(targetId, {
      reliable: true
    });
    
    this.setupConnection(conn, myName);
  }

  private setupConnection(conn: DataConnection, myName: string) {
    this.conn = conn;

    this.conn.on('open', () => {
      console.log('Data Channel Open!');
      this.emit('status', 'connected');
      
      // Send handshake
      this.sendJSON({ type: 'handshake', name: myName });
    });

    this.conn.on('data', (data) => {
      this.handleDataMessage(data);
    });

    this.conn.on('close', () => {
      console.log('Connection Closed');
      this.emit('partner-disconnected', true);
      this.conn = null;
    });

    this.conn.on('error', (err) => {
      console.error('Connection Error:', err);
      this.emit('error', 'Connection lost');
      this.conn = null;
    });
  }

  private handleDataMessage(data: any) {
    // More robust check for binary data types (ArrayBuffer, Uint8Array, Blob)
    const isBinary = data instanceof ArrayBuffer || data instanceof Uint8Array || data instanceof Blob;

    if (isBinary) {
      this.handleFileChunk(data);
      return;
    }

    let parsed = data;
    if (typeof data === 'string') {
        try {
            parsed = JSON.parse(data);
        } catch(e) { console.error("Parse error", e); return; }
    }

    switch (parsed.type) {
        case 'handshake':
          this.emit('handshake-received', parsed);
          break;
        case 'chat':
          this.emit('message', parsed.message);
          break;
        case 'file-meta':
          this.prepareIncomingFile(parsed.meta);
          break;
        case 'error':
            this.emit('error', parsed.message);
            break;
    }
  }

  public sendText(text: string, senderId: string, senderName: string): ChatMessage {
    const message: ChatMessage = {
      id: crypto.randomUUID(),
      senderId,
      senderName,
      text,
      timestamp: Date.now(),
      type: 'text'
    };
    this.sendJSON({ type: 'chat', message });
    return message;
  }

  private sendJSON(data: any) {
    if (this.conn && this.conn.open) {
      this.conn.send(data);
    } else {
        console.warn("Attempted to send data but connection is not open");
    }
  }

  public async sendFile(file: File, senderId: string, senderName: string) {
    if (!this.conn || !this.conn.open) return;

    const fileId = crypto.randomUUID();
    const meta: FileTransferMeta = {
      id: fileId,
      name: file.name,
      size: file.size,
      type: file.type,
      totalChunks: Math.ceil(file.size / CHUNK_SIZE)
    };

    this.sendJSON({ type: 'file-meta', meta });

    const message: ChatMessage = {
      id: fileId,
      senderId,
      senderName,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      timestamp: Date.now(),
      type: (file.type.startsWith('image/') ? 'image' : 'file') as MessageType
    };
    this.emit('message-sent', message);

    const reader = new FileReader();
    let offset = 0;

    const readSlice = (o: number) => {
      const slice = file.slice(offset, o + CHUNK_SIZE);
      reader.readAsArrayBuffer(slice);
    };

    reader.onload = (e) => {
      if (!this.conn || !this.conn.open) return;
      
      const buffer = e.target?.result as ArrayBuffer;
      this.conn.send(buffer);
      
      offset += buffer.byteLength;

      const progress = Math.min(100, Math.round((offset / file.size) * 100));
      this.emit('file-progress', { id: fileId, fileName: file.name, progress, direction: 'outgoing' });

      if (offset < file.size) {
        setTimeout(() => readSlice(offset), 0);
      } else {
        this.emit('file-progress', { id: fileId, fileName: file.name, progress: 100, direction: 'outgoing' });
      }
    };

    readSlice(0);
    return message;
  }

  private prepareIncomingFile(meta: FileTransferMeta) {
    this.incomingFileMeta = meta;
    this.incomingFileBuffer = [];
    this.receivedBytes = 0;
    this.emit('file-progress', { id: meta.id, fileName: meta.name, progress: 0, direction: 'incoming' });
  }

  private handleFileChunk(data: ArrayBuffer | Uint8Array | Blob) {
    if (!this.incomingFileMeta) {
      console.warn("Received binary chunk but no file metadata present.");
      return;
    }

    // Determine size and store data
    let size = 0;
    if (data instanceof ArrayBuffer) {
        size = data.byteLength;
    } else if (data instanceof Uint8Array) {
        size = data.byteLength;
    } else if (data instanceof Blob) {
        size = data.size;
    }

    this.incomingFileBuffer.push(data);
    this.receivedBytes += size;

    const progress = Math.min(100, Math.round((this.receivedBytes / this.incomingFileMeta.size) * 100));
    this.emit('file-progress', { id: this.incomingFileMeta.id, fileName: this.incomingFileMeta.name, progress, direction: 'incoming' });

    if (this.receivedBytes >= this.incomingFileMeta.size) {
      // Create final blob
      const blob = new Blob(this.incomingFileBuffer, { type: this.incomingFileMeta.type });
      const url = URL.createObjectURL(blob);
      
      const message: ChatMessage = {
        id: this.incomingFileMeta.id,
        senderId: 'partner',
        senderName: 'Partner',
        fileUrl: url,
        fileName: this.incomingFileMeta.name,
        fileSize: this.incomingFileMeta.size,
        fileType: this.incomingFileMeta.type,
        timestamp: Date.now(),
        type: (this.incomingFileMeta.type.startsWith('image/') ? 'image' : 'file') as MessageType
      };

      this.emit('message', message);
      this.incomingFileMeta = null;
      this.incomingFileBuffer = [];
      this.receivedBytes = 0;
    }
  }

  public disconnect() {
    if (this.conn) {
        this.conn.close();
        this.conn = null;
    }
    if (this.peer) {
        this.peer.destroy();
        this.peer = null;
    }
    this.callbacks.clear();
  }
}

export const p2pService = new P2PService();
